/**
 * European Countries Data
 * Contains phone prefix information, validation rules, and flags for all European countries
 */

export interface CountryData {
  code: string;           // ISO 3166-1 alpha-2 code (e.g., "RO")
  name: string;           // Country name (e.g., "Romania")
  prefix: string;         // International prefix with + (e.g., "+40")
  flag: string;           // Emoji flag (e.g., "🇷🇴")
  mobilePrefix: string[]; // Valid mobile prefixes after country code (e.g., ["7"])
  lengthMin: number;      // Minimum total length including country prefix
  lengthMax: number;      // Maximum total length including country prefix
}

/**
 * Complete list of European countries with phone validation rules
 * Includes EU members, EEA, and other European countries
 */
export const EUROPEAN_COUNTRIES: CountryData[] = [
  // EU Member States
  {
    code: 'AT',
    name: 'Austria',
    prefix: '+43',
    flag: '🇦🇹',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'BE',
    name: 'Belgium',
    prefix: '+32',
    flag: '🇧🇪',
    mobilePrefix: ['4'],
    lengthMin: 11,
    lengthMax: 12,
  },
  {
    code: 'BG',
    name: 'Bulgaria',
    prefix: '+359',
    flag: '🇧🇬',
    mobilePrefix: ['8', '9'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'HR',
    name: 'Croatia',
    prefix: '+385',
    flag: '🇭🇷',
    mobilePrefix: ['9'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'CY',
    name: 'Cyprus',
    prefix: '+357',
    flag: '🇨🇾',
    mobilePrefix: ['9'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
    prefix: '+420',
    flag: '🇨🇿',
    mobilePrefix: ['6', '7'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'DK',
    name: 'Denmark',
    prefix: '+45',
    flag: '🇩🇰',
    mobilePrefix: ['2', '3', '4', '5', '6', '7', '9'],
    lengthMin: 11,
    lengthMax: 11,
  },
  {
    code: 'EE',
    name: 'Estonia',
    prefix: '+372',
    flag: '🇪🇪',
    mobilePrefix: ['5'],
    lengthMin: 11,
    lengthMax: 12,
  },
  {
    code: 'FI',
    name: 'Finland',
    prefix: '+358',
    flag: '🇫🇮',
    mobilePrefix: ['4', '5'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'FR',
    name: 'France',
    prefix: '+33',
    flag: '🇫🇷',
    mobilePrefix: ['6', '7'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'DE',
    name: 'Germany',
    prefix: '+49',
    flag: '🇩🇪',
    mobilePrefix: ['15', '16', '17'],
    lengthMin: 12,
    lengthMax: 14,
  },
  {
    code: 'GR',
    name: 'Greece',
    prefix: '+30',
    flag: '🇬🇷',
    mobilePrefix: ['69'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'HU',
    name: 'Hungary',
    prefix: '+36',
    flag: '🇭🇺',
    mobilePrefix: ['20', '30', '31', '50', '70'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'IE',
    name: 'Ireland',
    prefix: '+353',
    flag: '🇮🇪',
    mobilePrefix: ['8'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'IT',
    name: 'Italy',
    prefix: '+39',
    flag: '🇮🇹',
    mobilePrefix: ['3'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'LV',
    name: 'Latvia',
    prefix: '+371',
    flag: '🇱🇻',
    mobilePrefix: ['2'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'LT',
    name: 'Lithuania',
    prefix: '+370',
    flag: '🇱🇹',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'LU',
    name: 'Luxembourg',
    prefix: '+352',
    flag: '🇱🇺',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'MT',
    name: 'Malta',
    prefix: '+356',
    flag: '🇲🇹',
    mobilePrefix: ['7', '9'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'NL',
    name: 'Netherlands',
    prefix: '+31',
    flag: '🇳🇱',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'PL',
    name: 'Poland',
    prefix: '+48',
    flag: '🇵🇱',
    mobilePrefix: ['5', '6', '7', '8'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'PT',
    name: 'Portugal',
    prefix: '+351',
    flag: '🇵🇹',
    mobilePrefix: ['9'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'RO',
    name: 'Romania',
    prefix: '+40',
    flag: '🇷🇴',
    mobilePrefix: ['7'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'SK',
    name: 'Slovakia',
    prefix: '+421',
    flag: '🇸🇰',
    mobilePrefix: ['9'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'SI',
    name: 'Slovenia',
    prefix: '+386',
    flag: '🇸🇮',
    mobilePrefix: ['3', '4', '5', '6', '7'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'ES',
    name: 'Spain',
    prefix: '+34',
    flag: '🇪🇸',
    mobilePrefix: ['6', '7'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'SE',
    name: 'Sweden',
    prefix: '+46',
    flag: '🇸🇪',
    mobilePrefix: ['7'],
    lengthMin: 12,
    lengthMax: 13,
  },

  // EEA Countries (non-EU)
  {
    code: 'IS',
    name: 'Iceland',
    prefix: '+354',
    flag: '🇮🇸',
    mobilePrefix: ['6', '7', '8'],
    lengthMin: 11,
    lengthMax: 11,
  },
  {
    code: 'LI',
    name: 'Liechtenstein',
    prefix: '+423',
    flag: '🇱🇮',
    mobilePrefix: ['7'],
    lengthMin: 11,
    lengthMax: 12,
  },
  {
    code: 'NO',
    name: 'Norway',
    prefix: '+47',
    flag: '🇳🇴',
    mobilePrefix: ['4', '9'],
    lengthMin: 11,
    lengthMax: 11,
  },

  // Other European Countries
  {
    code: 'AL',
    name: 'Albania',
    prefix: '+355',
    flag: '🇦🇱',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'AD',
    name: 'Andorra',
    prefix: '+376',
    flag: '🇦🇩',
    mobilePrefix: ['3', '6'],
    lengthMin: 10,
    lengthMax: 10,
  },
  {
    code: 'AM',
    name: 'Armenia',
    prefix: '+374',
    flag: '🇦🇲',
    mobilePrefix: ['4', '5', '7', '9'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'AZ',
    name: 'Azerbaijan',
    prefix: '+994',
    flag: '🇦🇿',
    mobilePrefix: ['4', '5', '7'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'BY',
    name: 'Belarus',
    prefix: '+375',
    flag: '🇧🇾',
    mobilePrefix: ['25', '29', '33', '44'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'BA',
    name: 'Bosnia and Herzegovina',
    prefix: '+387',
    flag: '🇧🇦',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'GE',
    name: 'Georgia',
    prefix: '+995',
    flag: '🇬🇪',
    mobilePrefix: ['5'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    prefix: '+44',
    flag: '🇬🇧',
    mobilePrefix: ['7'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'XK',
    name: 'Kosovo',
    prefix: '+383',
    flag: '🇽🇰',
    mobilePrefix: ['4', '5'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'MK',
    name: 'North Macedonia',
    prefix: '+389',
    flag: '🇲🇰',
    mobilePrefix: ['7'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'MD',
    name: 'Moldova',
    prefix: '+373',
    flag: '🇲🇩',
    mobilePrefix: ['6', '7'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'MC',
    name: 'Monaco',
    prefix: '+377',
    flag: '🇲🇨',
    mobilePrefix: ['4', '6'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'ME',
    name: 'Montenegro',
    prefix: '+382',
    flag: '🇲🇪',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'RS',
    name: 'Serbia',
    prefix: '+381',
    flag: '🇷🇸',
    mobilePrefix: ['6'],
    lengthMin: 12,
    lengthMax: 13,
  },
  {
    code: 'SM',
    name: 'San Marino',
    prefix: '+378',
    flag: '🇸🇲',
    mobilePrefix: ['3', '6'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'CH',
    name: 'Switzerland',
    prefix: '+41',
    flag: '🇨🇭',
    mobilePrefix: ['7'],
    lengthMin: 12,
    lengthMax: 12,
  },
  {
    code: 'TR',
    name: 'Turkey',
    prefix: '+90',
    flag: '🇹🇷',
    mobilePrefix: ['5'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'UA',
    name: 'Ukraine',
    prefix: '+380',
    flag: '🇺🇦',
    mobilePrefix: ['5', '6', '7', '9'],
    lengthMin: 13,
    lengthMax: 13,
  },
  {
    code: 'VA',
    name: 'Vatican City',
    prefix: '+39',
    flag: '🇻🇦',
    mobilePrefix: ['3'],
    lengthMin: 12,
    lengthMax: 13,
  },
];

/**
 * Default country code to use as fallback
 */
export const DEFAULT_COUNTRY_CODE = 'RO';

/**
 * Get a country by its ISO code
 * @param code - ISO 3166-1 alpha-2 code
 * @returns CountryData or undefined if not found
 */
export function getCountryByCode(code: string): CountryData | undefined {
  return EUROPEAN_COUNTRIES.find(country => country.code.toUpperCase() === code.toUpperCase());
}

/**
 * Get a country by its phone prefix
 * @param prefix - Phone prefix (with or without +)
 * @returns CountryData or undefined if not found
 */
export function getCountryByPrefix(prefix: string): CountryData | undefined {
  const normalizedPrefix = prefix.startsWith('+') ? prefix : `+${prefix}`;
  return EUROPEAN_COUNTRIES.find(country => country.prefix === normalizedPrefix);
}

/**
 * Filter countries by allowed codes
 * @param allowedCodes - Array of ISO codes, comma-separated string, or null/undefined for all
 * @returns Filtered array of CountryData
 */
export function getFilteredCountries(allowedCodes?: string[] | string | null): CountryData[] {
  if (!allowedCodes || (Array.isArray(allowedCodes) && allowedCodes.length === 0)) {
    return EUROPEAN_COUNTRIES;
  }

  // Handle comma-separated string (from HTML attributes)
  let codesArray: string[];
  if (typeof allowedCodes === 'string') {
    codesArray = allowedCodes.split(',').map(code => code.trim()).filter(code => code.length > 0);
  } else {
    codesArray = allowedCodes;
  }

  if (codesArray.length === 0) {
    return EUROPEAN_COUNTRIES;
  }

  const upperCodes = codesArray.map(code => code.toUpperCase());
  return EUROPEAN_COUNTRIES.filter(country => upperCodes.includes(country.code.toUpperCase()));
}

/**
 * Validate a phone number for a specific country
 * @param phoneNumber - Phone number without country prefix
 * @param country - CountryData to validate against
 * @returns Validation result object
 */
export function validatePhoneNumber(
  phoneNumber: string,
  country: CountryData
): { isValid: boolean; error?: string } {
  // Remove any non-digit characters
  const cleanNumber = phoneNumber.replace(/\D/g, '');

  // Calculate the full number length (prefix digits + phone number)
  const prefixDigits = country.prefix.replace('+', '');
  const fullLength = prefixDigits.length + cleanNumber.length + 1; // +1 for the '+'

  // Check minimum length
  if (fullLength < country.lengthMin) {
    return {
      isValid: false,
      error: `Phone number is too short. Expected at least ${country.lengthMin - prefixDigits.length - 1} digits.`,
    };
  }

  // Check maximum length
  if (fullLength > country.lengthMax) {
    return {
      isValid: false,
      error: `Phone number is too long. Expected at most ${country.lengthMax - prefixDigits.length - 1} digits.`,
    };
  }

  // Check mobile prefix
  if (cleanNumber.length > 0) {
    const hasValidMobilePrefix = country.mobilePrefix.some(prefix => cleanNumber.startsWith(prefix));
    if (!hasValidMobilePrefix) {
      return {
        isValid: false,
        error: `Invalid mobile prefix. Valid prefixes for ${country.name}: ${country.mobilePrefix.join(', ')}`,
      };
    }
  }

  return { isValid: true };
}

/**
 * Get the full phone number with country prefix
 * @param phoneNumber - Phone number without country prefix
 * @param country - CountryData
 * @returns Full phone number with prefix
 */
export function getFullPhoneNumber(phoneNumber: string, country: CountryData): string {
  const cleanNumber = phoneNumber.replace(/\D/g, '');
  return `${country.prefix}${cleanNumber}`;
}

/**
 * Parse a full phone number and extract country and local number
 * @param fullNumber - Full phone number with country prefix
 * @returns Object with country and local number, or null if not found
 */
export function parsePhoneNumber(fullNumber: string): { country: CountryData; localNumber: string } | null {
  const cleanNumber = fullNumber.replace(/\D/g, '');
  const withPlus = fullNumber.startsWith('+') ? fullNumber : `+${cleanNumber}`;

  // Sort countries by prefix length (longest first) to match correctly
  const sortedCountries = [...EUROPEAN_COUNTRIES].sort(
    (a, b) => b.prefix.length - a.prefix.length
  );

  for (const country of sortedCountries) {
    if (withPlus.startsWith(country.prefix)) {
      const localNumber = withPlus.slice(country.prefix.length);
      return { country, localNumber };
    }
  }

  return null;
}
